import React from "react";
import { usStates } from "../../constants/usStates";

const canadianProvinces = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "YT", label: "Yukon" },
];

const countries = [
  { value: "United States", label: "United States" },
  { value: "Canada", label: "Canada" },
];

/**
 * AddressFields component that handles both Canadian and US addresses
 * Dynamically switches labels and region options based on selected country
 *
 * @param {Object} address - The address object with street, city, state, zipCode, country
 * @param {Function} onChange - Callback function when any address field changes (field, value)
 * @param {Object} errors - Object containing field error messages
 * @param {boolean} disabled - Whether the fields are disabled
 * @param {string} inputClassName - Custom className for input fields
 */
function AddressFields({
  address = {},
  onChange,
  errors = {},
  disabled = false,
  inputClassName = "block w-full px-3 py-2 text-sm border rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
}) {
  const isCanada = address.country === "Canada";
  const regions = isCanada ? canadianProvinces : usStates;

  // Dynamic labels based on country
  const regionLabel = isCanada ? "Province" : "State";
  const zipLabel = isCanada ? "Postal Code" : "Zip Code";
  const postalPlaceholder = isCanada ? "M5V 3A8" : "12345";
  const cityPlaceholder = isCanada ? "Toronto" : "New York";

  const handleChange = (field, value) => {
    // When country changes, clear state if switching countries
    if (field === "country") {
      onChange("country", value);
      // Clear state when switching countries since the options change
      if ((value === "United States" && isCanada) || (value === "Canada" && !isCanada)) {
        onChange("state", "");
      }
    } else {
      onChange(field, value);
    }
  };

  const getInputClassName = (fieldName) => {
    const hasError = errors[fieldName] || errors[`address.${fieldName}`];
    return `${inputClassName} ${hasError ? "border-red-300" : "border-gray-300"}`;
  };

  const getError = (fieldName) => {
    return errors[fieldName] || errors[`address.${fieldName}`];
  };

  return (
    <div className="space-y-3">
      {/* Street Address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Street Address *
        </label>
        <input
          type="text"
          value={address.street || ""}
          onChange={(e) => handleChange("street", e.target.value)}
          disabled={disabled}
          className={getInputClassName("street")}
          placeholder="123 Main Street"
        />
        {getError("street") && (
          <p className="text-sm text-red-600 mt-1">{getError("street")}</p>
        )}
      </div>

      {/* City, State, Postal/Zip, Country */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City *
          </label>
          <input
            type="text"
            value={address.city || ""}
            onChange={(e) => handleChange("city", e.target.value)}
            disabled={disabled}
            className={getInputClassName("city")}
            placeholder={cityPlaceholder}
          />
          {getError("city") && (
            <p className="text-sm text-red-600 mt-1">{getError("city")}</p>
          )}
        </div>

        {/* State */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {regionLabel} *
          </label>
          <select
            value={address.state || ""}
            onChange={(e) => handleChange("state", e.target.value)}
            disabled={disabled}
            className={getInputClassName("state")}
          >
            <option value="">Select {regionLabel}</option>
            {regions.map((region) => (
              <option key={region.value} value={region.value}>
                {region.label}
              </option>
            ))}
          </select>
          {getError("state") && (
            <p className="text-sm text-red-600 mt-1">{getError("state")}</p>
          )}
        </div>

        {/* Zip Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {zipLabel} *
          </label>
          <input
            type="text"
            value={address.zipCode || ""}
            onChange={(e) => handleChange("zipCode", e.target.value)}
            disabled={disabled}
            className={getInputClassName("zipCode")}
            placeholder={postalPlaceholder}
          />
          {getError("zipCode") && (
            <p className="text-sm text-red-600 mt-1">{getError("zipCode")}</p>
          )}
        </div>

        {/* Country */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country *
          </label>
          <select
            value={address.country || "United States"}
            onChange={(e) => handleChange("country", e.target.value)}
            disabled={disabled}
            className={`${inputClassName} border-gray-300`}
          >
            {countries.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/**
 * Validate zip code format based on country
 * @param {string} zipCode - The zip/postal code to validate
 * @param {string} country - "Canada" or "United States"
 * @returns {string|null} - Error message or null if valid
 */
export function validateZipCode(zipCode, country) {
  if (!zipCode || !zipCode.trim()) {
    return country === "United States" ? "Zip code is required" : "Postal code is required";
  }

  const trimmed = zipCode.trim().toUpperCase();

  if (country === "United States") {
    // US Zip: 12345 or 12345-6789
    const usZipRegex = /^\d{5}(-\d{4})?$/;
    if (!usZipRegex.test(trimmed)) {
      return "Please enter a valid zip code (e.g., 12345 or 12345-6789)";
    }
  } else {
    // Canadian postal code: A1A 1A1 or A1A1A1
    const caPostalRegex = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/;
    if (!caPostalRegex.test(trimmed)) {
      return "Please enter a valid postal code (e.g., M5V 3A8)";
    }
  }

  return null;
}

/**
 * Get display labels based on country
 * @param {string} country - "Canada" or "United States"
 * @returns {Object} - Object with regionLabel and postalLabel
 */
export function getAddressLabels(country) {
  const isCanada = country === "Canada";
  return {
    regionLabel: isCanada ? "Province" : "State",
    postalLabel: isCanada ? "Postal Code" : "Zip Code",
  };
}

export default AddressFields;
